import {
  ProductColorVariation,
  ProductPrice,
  ProductPriceColour,
  ProductPriceColourSKU,
  ProductPrices,
  ProductPriceViewModel,
  PRICE_STATE_FROM,
  PRICE_STATE_NOW_FROM,
  PRICE_STATE_NOW,
  PRICE_STATE_WAS,
  PRICE_STATE_RRP,
  PRICE_TYPE_SELL,
  PRICE_TYPE_STD,
  PRICE_TYPE_EM,
  PRICE_TYPE_RRP,
  StringTMap,
  PRICE_TYPE,
  PRICE_TYPE_LOWEST,
} from '@as-react/common-types';
import { isNullOrUndefined } from './general.util';

const PRICE_STATUS_VISUAL: StringTMap<StringTMap<string>> = {
  SELL: {
    '1': PRICE_STATE_FROM,
    '3': PRICE_STATE_FROM,
    '4': PRICE_STATE_NOW,
    '5': PRICE_STATE_NOW_FROM,
  },
  STD: { '1': PRICE_STATE_FROM, '4': PRICE_STATE_NOW, '5': PRICE_STATE_WAS },
  RRP: { '2': PRICE_STATE_RRP, '3': PRICE_STATE_RRP },
  EM: {},
};

const checkIfEmPriceIsLowest = (productPrices: ProductPrices) => {
  let emIsLowestPrice = false;

  const allOtherPrices = (Object.keys(productPrices) as Array<keyof typeof productPrices>)
    .filter(productPriceKey => productPriceKey !== 'EM')
    .map(productPriceKey => productPrices[productPriceKey]);

  allOtherPrices.forEach(
    otherPrice => (emIsLowestPrice = emIsLowestPrice && productPrices.EM!.original < otherPrice!.original)
  );

  return emIsLowestPrice;
};

const generatePriceStateLabel = (priceType: PRICE_TYPE, priceState: number): string | undefined => {
  if (
    !isNullOrUndefined(PRICE_STATUS_VISUAL[priceType]) &&
    !isNullOrUndefined(PRICE_STATUS_VISUAL[priceType]?.[`${priceState}`])
  ) {
    return PRICE_STATUS_VISUAL[priceType]![`${priceState}`];
  }

  return undefined;
};

const generatePriceViewModelObject = (
  price: number,
  priceType: PRICE_TYPE,
  priceState: number
): ProductPriceViewModel => ({
  intPrice: undefined,
  decPrice: undefined,
  original: price,
  priceState: generatePriceStateLabel(priceType, priceState),
});

export const mapEdgePriceToPricesObject = (oldPrice: ProductPriceColour | ProductPriceColourSKU): ProductPrices => {
  const productPrices = {
    SELL: generatePriceViewModelObject(oldPrice.sellPrice, PRICE_TYPE_SELL, oldPrice.priceState),
    STD: oldPrice.standardPrice
      ? generatePriceViewModelObject(oldPrice.standardPrice, PRICE_TYPE_STD, oldPrice.priceState)
      : undefined,
    RRP: oldPrice.rrpPrice
      ? generatePriceViewModelObject(oldPrice.rrpPrice, PRICE_TYPE_RRP, oldPrice.priceState)
      : undefined,
    EM: oldPrice.emPrice
      ? generatePriceViewModelObject(oldPrice.emPrice, PRICE_TYPE_EM, oldPrice.priceState)
      : undefined,
    LOWEST: oldPrice.lowestPrice
      ? generatePriceViewModelObject(oldPrice.lowestPrice, PRICE_TYPE_LOWEST, oldPrice.priceState)
      : undefined,
  };

  switch (oldPrice.priceState) {
    case 0:
    case 1:
      delete productPrices.STD;
      delete productPrices.RRP;
      break;
    case 2:
    case 3:
      delete productPrices.STD;
      if (!isNullOrUndefined(productPrices.RRP) && productPrices.RRP.original <= productPrices.SELL.original) {
        delete productPrices.RRP;
      }
      break;
    case 4:
    case 5:
      delete productPrices.RRP;
      break;
  }

  if (!checkIfEmPriceIsLowest(productPrices)) {
    delete productPrices.EM;
  }

  return productPrices;
};

export const setColorPrices = (productPrice: ProductPrice, colorId: number): Partial<ProductColorVariation> => {
  const colorPrice = productPrice.colours.find(item => item.colourId === colorId);
  if (isNullOrUndefined(colorPrice)) {
    return {};
  }

  return {
    hasDifferentPrices: colorPrice.colourHasDifferentPrices,
    hasDiscount:
      colorPrice.priceState === 2 ||
      colorPrice.priceState === 3 ||
      colorPrice.priceState === 4 ||
      colorPrice.priceState === 5,
    markdown: colorPrice.markdown,
    prices: mapEdgePriceToPricesObject(colorPrice),
    priceOverlay: {
      overlayCode: colorPrice.webThumbOverlayCode,
      overlayThumb: colorPrice.webThumbOverlayPicture,
    },
  };
};

export const prefixPriceWithCurrency = (value: string | number, currency: string) =>
  Number(value) < 0 ? `- ${currency}${Math.abs(Number(value))}` : `${currency}${value}`;

export const getRoundedPrice = (value: number): number => Math.round((value + Number.EPSILON) * 100) / 100;

export const showLowestPriceLastMonth = (
  enabled: boolean,
  lowestPrice?: ProductPriceViewModel['original']
): lowestPrice is ProductPriceViewModel['original'] => enabled && !!lowestPrice;

export const getOriginalPrices = (prices: ProductPrices) => ({
  STD: prices.STD?.original,
  RRP: prices.RRP?.original,
  LOWEST: prices.LOWEST?.original,
  SELL: prices.SELL?.original,
  EM: prices.EM?.original,
});

export const showPriceDiscount = (prices: ProductPrices | undefined, enablePriceReference: boolean) => {
  if (showLowestPriceLastMonth(Boolean(enablePriceReference), prices?.LOWEST?.original)) {
    const { SELL, LOWEST } = getOriginalPrices(prices);

    return LOWEST! > SELL;
  }

  return true;
};
